import React from 'react';
import {
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  fetchAdminDashboardStats,
  fetchActivitiesToValidate,
  fetchPaymentsToPay,
  fetchPendingCandidatures,
} from '../api/dashboard';
import { listUsers } from '../api/users';
import { listPostsSummary } from '../api/posts';

const formatDate = (date?: Date) =>
  date ? date.toLocaleDateString() : '—';

const formatAmount = (amount?: number) =>
  amount != null ? `${amount.toFixed(0)} CFA` : '—';

export const DashboardPage: React.FC = () => {
  const { data: stats } = useQuery({
    queryKey: ['admin-dashboard-stats'],
    queryFn: fetchAdminDashboardStats,
  });

  const { data: pendingCandidatures } = useQuery({
    queryKey: ['pending-candidatures'],
    queryFn: () => fetchPendingCandidatures(5),
  });

  const { data: activitiesToValidate } = useQuery({
    queryKey: ['activities-to-validate'],
    queryFn: () => fetchActivitiesToValidate(5),
  });

  const { data: paymentsToPay } = useQuery({
    queryKey: ['payments-to-pay'],
    queryFn: () => fetchPaymentsToPay(5),
  });

  const { data: users } = useQuery({
    queryKey: ['users-for-dashboard'],
    queryFn: listUsers,
  });

  const { data: posts } = useQuery({
    queryKey: ['posts-summary-for-dashboard'],
    queryFn: listPostsSummary,
  });

  const userNameById = React.useMemo(() => {
    const map: Record<string, string> = {};
    users?.forEach((u) => {
      const fullName = `${u.lastName ?? ''} ${u.firstName ?? ''}`.trim();
      map[u.id] = fullName || u.email || u.id;
    });
    return map;
  }, [users]);

  const postTitleById = React.useMemo(() => {
    const map: Record<string, string> = {};
    posts?.forEach((p) => {
      map[p.id] = p.title || p.id;
    });
    return map;
  }, [posts]);

  const candidatureLabelById = React.useMemo(() => {
    const map: Record<string, string> = {};
    pendingCandidatures?.forEach((c) => {
      const name = c.userId ? userNameById[c.userId] ?? c.userId : '';
      const postTitle = c.postId ? postTitleById[c.postId] ?? c.postId : '';
      const labelParts = [name, postTitle].filter(Boolean);
      if (labelParts.length) {
        map[c.id] = labelParts.join(' · ');
      }
    });
    return map;
  }, [pendingCandidatures, userNameById, postTitleById]);

  return (
    <Grid container spacing={3}>
      <Grid size={12}>
        <Typography variant="h5" gutterBottom>
          Dashboard
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Total candidatures
          </Typography>
          <Typography variant="h4">
            {stats?.candidaturesCount ?? '—'}
          </Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Candidatures en attente
          </Typography>
          <Typography variant="h4">
            {stats?.candidaturesPendingCount ?? '—'}
          </Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Écoles
          </Typography>
          <Typography variant="h4">
            {stats?.schoolsCount ?? '—'}
          </Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Postes
          </Typography>
          <Typography variant="h4">
            {stats?.postsCount ?? '—'}
          </Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Activités validées
          </Typography>
          <Typography variant="h4">
            {stats?.activitiesValidatedCount ?? '—'}
          </Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Paiements
          </Typography>
          <Typography variant="h4">
            {stats?.paymentsCount ?? '—'}
          </Typography>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Paper sx={{ p: 2, height: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            Candidatures en attente
          </Typography>
          <List dense>
            {pendingCandidatures?.map((c) => (
              <React.Fragment key={c.id}>
                <ListItem>
                  <ListItemText
                    primary={
                      c.userId
                        ? userNameById[c.userId] ?? 'Candidature'
                        : 'Candidature'
                    }
                    secondary={`Poste: ${
                      c.postId ? postTitleById[c.postId] ?? c.postId : '—'
                    } · Date: ${formatDate(c.date)}`}
                  />
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            )) || (
              <Typography variant="body2" color="text.secondary">
                Aucune candidature en attente.
              </Typography>
            )}
          </List>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Paper sx={{ p: 2, height: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            Activités à valider
          </Typography>
          <List dense>
            {activitiesToValidate?.map((a) => (
              <React.Fragment key={a.id}>
                <ListItem>
                  <ListItemText
                    primary="Activité à valider"
                    secondary={`Candidature: ${
                      a.candidatureId
                        ? candidatureLabelById[a.candidatureId] ??
                          a.candidatureId
                        : '—'
                    } · Date: ${formatDate(a.date)}`}
                  />
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            )) || (
              <Typography variant="body2" color="text.secondary">
                Aucune activité en attente.
              </Typography>
            )}
          </List>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Paper sx={{ p: 2, height: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            Paiements à traiter
          </Typography>
          <List dense>
            {paymentsToPay?.map((p) => (
              <React.Fragment key={p.id}>
                <ListItem>
                  <ListItemText
                    primary="Paiement à traiter"
                    secondary={`Montant: ${formatAmount(
                      p.amount
                    )} · Statut: ${p.status ?? '—'} · Date: ${formatDate(
                      p.createdAt
                    )}`}
                  />
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            )) || (
              <Typography variant="body2" color="text.secondary">
                Aucun paiement en attente.
              </Typography>
            )}
          </List>
        </Paper>
      </Grid>
    </Grid>
  );
};

